<script>
import mixins from '../mixins'
import moment from 'moment'
import Map from 'ol/Map.js'
import View from 'ol/View.js'
import TileLayer from 'ol/layer/Tile.js'
import OSM from 'ol/source/OSM.js'
import Feature from 'ol/Feature.js'
import Point from 'ol/geom/Point.js'
import { fromLonLat } from 'ol/proj.js'
import { Fill, Stroke, Style } from 'ol/style.js'
import VectorSource from 'ol/source/Vector.js'
import { Vector as VectorLayer } from 'ol/layer.js'
import CircleStyle from 'ol/style/Circle'
import Overlay from 'ol/Overlay.js'
import api from '@utils/api'
import green from '@assets/images/green_barrel.png'
import yellow from '@assets/images/yellow_barrel.png'
import red from '@assets/images/red_barrel.png'
import { unByKey } from 'ol/Observable'

export default {
  name: 'CurrentUsageOfOilOnAMap',
  mixins: [mixins],
  data() {
    return {
      time15MinutesAgo: moment().subtract(15, 'minutes'),
      map: null,
      points: [],
      remains: [],
      details: [],
      barrels: { green, yellow, red },
      info: [],
      showInfo: false,
      loading: false,
      infoLoading: false,
      currentAddress: {},
      vectors: [],
      columns: [
        { text: 'oil_type', value: 'oil', sortable: true },
        { text: 'drum_volume', value: 'initialAmount', sortable: true },
        { text: 'current_volume', value: 'currentAmount', sortable: true },
      ],
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.loading = true
      await this.getDataSet()
    },
    async getDataSet() {
      api.getReportData('current_usage_on_map').then(response => {
        response.map(data => {
          this.details = response
          this.addPoint(data)
        })
        this.createMap()
        this.loading = false
      })
      .catch(error => {
        this.loading = false
      })
    },
    getInfo(organisation) {
      const remains = this.remains.filter(
        elem => elem.organisation === parseInt(organisation)
      )
      let hasEventsInLast15Minutes =
        remains.filter(elem => elem.time >= this.time15MinutesAgo).length > 0
      return {
        organisation,
        hasEventsInLast15Minutes,
      }
    },
    addPoint(data) {
      const {
        latitude,
        longitude,
        minSufficientDays,
        organisation,
        text,
        hasEventsInLast15Minutes,
      } = data
      const color = this.getPointColor(minSufficientDays)
      if (!latitude || !longitude) {
        return
      }
      const point = new Feature({
        geometry: new Point(fromLonLat([longitude, latitude])),
        organisation,
        text,
        hasEventsInLast15Minutes,
        color,
      })
      point.setStyle(this.getPointStyle(color))
      this.points.push(point)
    },
    createMap() {
      const vectorSource = new VectorSource({
        features: [...this.points],
      })

      const vectorLayer = new VectorLayer({
        source: vectorSource,
      })

      this.map = new Map({
        layers: [
          new TileLayer({
            source: new OSM(),
          }),
          vectorLayer,
        ],
        target: 'map',
        view: new View({
          center: [0, 0],
          zoom: 2,
        }),
      })

      // set animation for recent remains
      vectorSource
        .getFeatures()
        .filter(feature => feature.getProperties().hasEventsInLast15Minutes)
        .forEach(feature => {
          this.flash(feature, this.map, 10000, feature.getProperties().color)
        })

      const element = document.getElementById('popup')
      const popup = new Overlay({
        element: element,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -50],
      })
      this.map.addOverlay(popup)

      this.map.on('click', evt => {
        const feature = this.map.forEachFeatureAtPixel(
          evt.pixel,
          feature => feature
        )
        if (feature) {
          this.showInfoDialog(feature.getProperties())
        }
      })

      // Zoom to points
      let extent = vectorLayer.getSource().getExtent()
      this.map.getView().fit(extent)
    },
    getPointColor(sufficientForDays) {
      if (sufficientForDays <= 1) {
        return 'rgba(255,0,0,1)'
      }
      if (sufficientForDays <= 7) {
        return 'rgba(255,219,31,1)'
      }
      return 'rgba(0,128,0,1)'
    },
    pointColorStyle(item) {
      return 'color:' + item.color
    },
    getPointStyle(color) {
      return new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color }),
          stroke: new Stroke({
            color,
            width: 1,
          }),
        }),
      })
    },
    flash(feature, map, duration, color) {
      let start = new Date()
      let listenerKey
      let self = this

      function animate(event) {
        let vectorContext = event.vectorContext
        let frameState = event.frameState
        let flashGeom = feature.getGeometry().clone()
        let elapsed = frameState.time - start
        let elapsedRatio = elapsed / duration
        let opacity = Math.abs(
          Math.cos(elapsedRatio * (duration / 1000) * Math.PI)
        )
        let style = self.getPointStyle(color.replace('1)', opacity + ')'))
        feature.setStyle(style)
        vectorContext.setStyle(style)
        vectorContext.drawGeometry(flashGeom)
        if (elapsed > duration) {
          unByKey(listenerKey)
          return
        }
        map.render()
      }

      listenerKey = map.on('postcompose', animate)
    },

    async showInfoDialog(data) {
      this.showInfo = true
      this.infoLoading = true
      const { organisation, text } = data
      this.info = await this.getInfoByOrganization(organisation)
      this.currentAddress = {
        organisation,
        text,
      }
      this.infoLoading = false
    },
    async getInfoByOrganization(id) {
      const result = []
      const dataByOrganization = this.details.find(
        elem => elem.organisation === Number(id)
      )
      dataByOrganization.oils.forEach(oilInfo => {
        const { oil, sufficiency, initialAmount, currentAmount } = oilInfo
        const color = this.getPointColor(sufficiency)
        result.push({
          color,
          oil,
          initialAmount,
          currentAmount,
        })
      })
      return result
    },
    close() {
      this.showInfo = false
      this.info = []
    },
  },
}
</script>

<template>
  <div
    id="map"
    class="full"
  >
    <v-progress-linear
      v-if="loading"
      slot="progress"
      height="2.2"
      color="blue"
      background-color="#E0E0E0"
      indeterminate
    />
    <div id="popup" />
    <v-dialog
      v-model="showInfo"
      persistent
      max-width="600"
    >
      <v-card>
        <v-card-title>
          <div>
            <div class="headline">
              <router-link
                v-if="userAllowedToNavigate && currentAddress.organisation"
                :to="{ name: 'organization', params: { orgId: currentAddress.organisation }} "> {{ getValue('organization', currentAddress.organisation) }}
              </router-link>
              <span v-if="!userAllowedToNavigate && currentAddress.organisation">{{ getValue('organization', currentAddress.organisation) }}</span>
              <span v-if="!currentAddress.organisation">{{ '-' }}</span>
            </div>
            <div class="subheading">{{ currentAddress.text }}</div>
          </div>
        </v-card-title>
        <v-card-text>

          <v-data-table
            :headers="columns"
            :items="info"
            :loading="infoLoading"
            :rows-per-page-items="rowsPerPage"
            :class="$style.sdTable"
            hide-actions
          >
            <template
              slot="headerCell"
              slot-scope="props">
              {{ $t(`label__${props.header.text}`) }}
            </template>
            <template
              slot="items"
              slot-scope="props"
            >
              <tr>
                <td>
                  <ul>
                    <li :style="pointColorStyle(props.item)">
                      <span>{{ getValue('goods', props.item.oil) }}</span>
                    </li>
                  </ul>
                </td>
                <td>{{ `${(props.item.initialAmount / unitsMeasurement.volumeMultiplier).toFixed(1)}` }}</td>
                <td>{{ `${(props.item.currentAmount / unitsMeasurement.volumeMultiplier).toFixed(1)}` }}</td>
              </tr>
            </template>
          </v-data-table>

        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="secondary"
            @click="close()"
          >
            {{ $t('buttons__close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style lang="scss" module>
@import url('https://openlayers.org/en/v5.3.0/css/ol.css');
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
  ul {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  li {
    text-indent: -1.4em;
    vertical-align: sub;
  }

  li::before {
    font-size: xx-large;
    content: '• ';
  }
  span {
    color: #000;
  }
}

.full {
  height: calc(100vh - 48px - 16px - 64px - 16px);
}

@keyframes blinker {
  50% {
    opacity: 1;
  }
}
</style>
